import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthUser } from '@/types';
import * as authService from '@/services/auth';
import { getAccessToken, setAccessToken, setOnAuthInvalidated } from '@/services/api';
import { queryClient } from '@/lib/queryClient';
import { useBasketStore } from '@/stores/basket';

// Session marker key - used to detect browser restart when "remember me" was not checked
// NOTE: We use localStorage with a timestamp instead of sessionStorage because sessionStorage
// is NOT shared between tabs, causing the old approach to incorrectly clear auth when opening new tabs.
const SESSION_START_KEY = 'pce-session-start';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes - if no activity for this long, consider it a new session
const HEARTBEAT_INTERVAL_MS = 60 * 1000; // Update session timestamp every minute

// Update session timestamp to indicate activity
function updateSessionTimestamp(): void {
  localStorage.setItem(SESSION_START_KEY, Date.now().toString());
}

// Check if this is a fresh browser session (not just a new tab)
// Uses a timestamp-based approach: if the last activity was more than SESSION_TIMEOUT_MS ago
// and we don't have a sessionStorage marker, treat it as a new browser session.
function checkSessionMarker(): boolean {
  // First check sessionStorage - if this tab has been active, it's the same session
  const tabMarker = sessionStorage.getItem('pce-tab-active');
  if (tabMarker) {
    return true; // This tab was already active, definitely same session
  }

  // Mark this tab as active
  sessionStorage.setItem('pce-tab-active', 'true');

  // Check localStorage for cross-tab session tracking
  const lastActivity = localStorage.getItem(SESSION_START_KEY);
  const now = Date.now();

  if (lastActivity) {
    const lastActivityTime = parseInt(lastActivity, 10);
    // If last activity was recent (within timeout), it's the same session
    // This handles the case of opening a new tab while already logged in
    if (now - lastActivityTime < SESSION_TIMEOUT_MS) {
      updateSessionTimestamp();
      return true;
    }
  }

  // Update the session start time
  updateSessionTimestamp();

  // No recent activity found - this is likely a new browser session
  return false;
}

// Start heartbeat to keep session alive across tabs
// This ensures that opening a new tab after extended browsing doesn't log the user out
function startSessionHeartbeat(): void {
  // Update immediately
  updateSessionTimestamp();

  // Then update every minute while this tab is open
  setInterval(updateSessionTimestamp, HEARTBEAT_INTERVAL_MS);
}

// Start heartbeat on module load
startSessionHeartbeat();

// Clear auth if browser was restarted and "remember me" wasn't checked
function clearAuthIfNewSession(): void {
  const rememberMe = localStorage.getItem('pce-remember-me') === 'true';
  const hasToken = !!getAccessToken();
  const isSameSession = checkSessionMarker();

  // If we have a token but "remember me" wasn't checked and this is a new browser session,
  // clear the auth state
  if (hasToken && !rememberMe && !isSameSession) {
    setAccessToken(null);
    localStorage.removeItem('pce-auth-storage');
  }
}

// Run on module load
clearAuthIfNewSession();

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isHydrated: boolean;
  hasCheckedAuth: boolean; // True after checkAuth has completed at least once
  error: string | null;

  // Actions
  login: (email: string, password: string, rememberMe?: boolean) => Promise<boolean>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  setHydrated: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true, // Start as true to prevent flash redirects before auth check completes
      isHydrated: false,
      hasCheckedAuth: false, // Start as false - must verify auth with backend
      error: null,

      login: async (email, password, rememberMe = false) => {
        set({ isLoading: true, error: null });

        try {
          const response = await authService.login({ email, password, rememberMe });

          if (response.success && response.user) {
            set({
              user: response.user,
              isAuthenticated: true,
              isLoading: false,
              hasCheckedAuth: true,
              error: null,
            });

            // Invalidate all cached queries — auth state affects what data is returned
            queryClient.invalidateQueries();

            // Sync basket from server (multi-device support)
            useBasketStore.getState().hydrateFromApi();

            return true;
          } else {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              hasCheckedAuth: true,
              error: response.error || 'Login failed',
            });
            return false;
          }
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Login failed';
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            hasCheckedAuth: true,
            error: message,
          });
          return false;
        }
      },

      logout: async () => {
        set({ isLoading: true });

        try {
          await authService.logout();
        } finally {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });

          // Clear all cached queries — prevents stale authenticated data leaking
          queryClient.clear();

          // Clear basket store on logout
          useBasketStore.getState().clearBasket();
        }
      },

      checkAuth: async () => {
        // Skip if we've already successfully checked auth this session
        // (login sets hasCheckedAuth, so we don't need to re-verify immediately)
        if (get().hasCheckedAuth && get().isAuthenticated) {
          set({ isLoading: false });
          return;
        }

        // Check if we have a token stored (from remember me or session)
        const hasToken = !!getAccessToken();

        // If no token and no persisted user, we're definitely not authenticated
        if (!hasToken && !get().user) {
          set({ isLoading: false, isAuthenticated: false, hasCheckedAuth: true });
          return;
        }

        // If we have persisted user state but no token, we need to clear it
        if (!hasToken && get().user) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            hasCheckedAuth: true,
          });
          return;
        }

        set({ isLoading: true });

        try {
          const user = await authService.getCurrentUser();

          if (user) {
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
              hasCheckedAuth: true,
            });
          } else {
            // API returned no user - clear everything
            setAccessToken(null);
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              hasCheckedAuth: true,
            });
          }
        } catch {
          // Auth check failed - clear everything
          setAccessToken(null);
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            hasCheckedAuth: true,
          });
        }
      },

      clearError: () => {
        set({ error: null });
      },

      setHydrated: () => {
        set({ isHydrated: true });
      },
    }),
    {
      name: 'pce-auth-storage',
      partialize: (state) => ({
        // Only persist user data, not loading states
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        // Called after hydration completes
        state?.setHydrated();
      },
    }
  )
);

// Register callback so the API interceptor can clear auth state when token refresh fails.
// This avoids circular imports (api.ts cannot import auth store directly).
setOnAuthInvalidated(() => {
  useAuthStore.setState({
    user: null,
    isAuthenticated: false,
    isLoading: false,
    hasCheckedAuth: true,
  });
});
